// react
import { useState, useEffect, useRef } from 'react';
import { FC } from 'react';
// material ui
import { 
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Select,
    MenuItem
} from '@mui/material';
// libra
import _ from 'lodash';
// Util
import Utils from '../common/utils';
// api
import {
    upsertSchedule,
    deleteSchedule
} from '../service/schedule-api';
// component
import ConfirmDialog from './confirm-dialog';
// type
import { Schedule } from '../types/schedule';
import { ChallengeResponse } from '../types/challengeResponse';
import { User } from '../types/user';

const ScheduleControlDialog: FC<any> = ( 
    props: { 
        open: boolean, 
        setOpen: any, 
        isNew: boolean,
        targetSchedule: Schedule, 
        setTargetSchedule: any,
        selectedDateTime: string, 
        selectedDisplayTime: string,
        schedules: Schedule[],
        setSchedules: any
    } 
) => {

    const { 
        open, 
        setOpen, 
        isNew,
        targetSchedule,
        setTargetSchedule,
        selectedDateTime, 
        selectedDisplayTime,
        schedules, 
        setSchedules
    } = props;

    // フォームの状態管理
    const [ formValues, setFormValues ] = useState({ title: "", userId: "" });
    const [ isOpenConfirm, setIsOpenConfirm ] = useState(false);
    const [ confirmCallBack, setConfirmCallBack ] = useState<(confirmed: boolean) => void>(() => {});
    const [ confirmTitle, setConfirmTitle ] = useState("");
    const [ confirmMessage, setConfirmMessage ] = useState("");
    const [ errors, setErrors ] = useState<{ [key: string]: string }>({});
    const [ usersSelect, setUsersSelect ] = useState<User[]>([ { userId: "", name: "", address: "", role: 'NORMAL' } ]);

    // targetEvent の値を初期化
    useEffect(() => {
        if (targetSchedule) {
            setFormValues({
                title: targetSchedule.title || "",
                userId: targetSchedule.userId || "",
            });
        }

        const authRaw = localStorage.getItem('auth');
        if (authRaw) {
            try {
                const authData: ChallengeResponse = JSON.parse(authRaw) as ChallengeResponse;
                const users = Array.isArray(authData.users) ? authData.users : [];
                setUsersSelect(users);
            } catch (error) {
                setUsersSelect([]);
            }
        }

    }, [targetSchedule, open]);


    /**
     * 🔴 確定ボタン押下時
     * @param event 
     * @returns 
     */
    const handleSubmit = (event: any) => {

        // バリデーション実施
        if (!validate()) return;

        // フォーム送信時にイベントを追加
        event.preventDefault();

        if (isNew) {
            // 新規登録時
            const id = new Date().getTime().toString();

            const newSchedule: Schedule = {
                id,
                title: formValues.title,
                start: selectedDateTime,
                userId: formValues.userId
            };

            setConfirmCallBack(() => async (isConfirmed: boolean) => {
                if (isConfirmed) {

                    const data = await upsertSchedule(newSchedule);
                    // 失敗した場合は処理終了
                    if (!data) {
                        return;
                    }
                    setSchedules(Utils.applyColorsToSchedules([...schedules, newSchedule]));
                    setIsOpenConfirm(false);
                    closeDialog();
                }
            });
            setConfirmTitle("予定作成確認");
            setConfirmMessage("予定を作成しますか？");
            setIsOpenConfirm(true);
            
        } else {
            // 更新時
            setConfirmCallBack(() => async (isConfirmed: boolean) => {
                if (isConfirmed) {
                    const margeSchedule = { ...targetSchedule, ...formValues };
                    // 更新時
                    const data = await upsertSchedule(margeSchedule);
                    // 失敗した場合は処理終了
                    if (!data) {
                        return;
                    }
                    const updateSchedules = _.map(schedules, (schedule: Schedule) => {
                        return schedule.id === targetSchedule.id ? margeSchedule : schedule;
                    });

                    setSchedules(Utils.applyColorsToSchedules(updateSchedules));
                    setIsOpenConfirm(false);
                    closeDialog();
                }
            });
            setConfirmTitle("予定更新確認");
            setConfirmMessage("予定を更新しますか？");
            setIsOpenConfirm(true);
        }
    }

    /**
     * バリデーション
     * @returns バリデーション結果(boolean)
     */
    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};
        if (!formValues.title.trim()) {
            newErrors.name = 'タイトルを入力してください。';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 入力変更を反映
    const handleChange = (event: any) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        });
    };

    /**
     * 🟡 閉じる
     */
    const closeDialog = () => {
        setErrors({});
        setFormValues({ title: "", userId: "" });
        setTargetSchedule(undefined);
        setOpen(false);
    }

    /**
     * 🔴 予定を削除
     * @param eventId 
     */
    const clickDelete = () => {

        const restoreSchedules = [...schedules];

        setConfirmCallBack(() => async (isConfirmed: boolean) => {
            if (isConfirmed) {

                const data = await deleteSchedule(targetSchedule.id);
                // 失敗した場合は処理終了
                if (!data) {
                    return;
                }

                const filterSchedules: Schedule[] = _.filter(restoreSchedules, (schedule: Schedule) => {
                    return schedule.id !== targetSchedule.id;
                })

                setSchedules(filterSchedules); // イベント情報更新
                setIsOpenConfirm(false); // 確認ダイアログ閉じる
                closeDialog(); // 入力ダイアログ閉じる
            }
        });
        setConfirmTitle("予定削除確認");
        setConfirmMessage("予定を削除しますか？");
        setIsOpenConfirm(true);
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={closeDialog}
            >
                <DialogTitle>予約情報入力</DialogTitle>
                <DialogContent>
                <DialogContentText>
                    予約情報を入力してください。
                </DialogContentText>
                <TextField
                    margin="dense"
                    id="start"
                    name="start"
                    label="時刻"
                    type="text"
                    fullWidth
                    value={selectedDisplayTime}
                    aria-readonly
                    multiline
                />

                <TextField
                    autoFocus
                    required
                    margin="dense"
                    id="title"
                    name="title"
                    label="タイトル"
                    type="text"
                    value={formValues.title}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    error={!!errors.name} 
                    helperText={errors.name} 
                />

                <Select
                    fullWidth
                    value={formValues.userId}
                    onChange={(e) => setFormValues({ ...formValues, userId: e.target.value as Schedule['userId'] })}
                >
                    {usersSelect.map((user: User) => (
                        <MenuItem
                            key={user.userId} 
                            value={user.userId}
                        >
                            {user.name}
                        </MenuItem>                            
                    ))}
                </Select>

                </DialogContent>
                <DialogActions>
                    {
                        !isNew && <Button onClick={clickDelete}>削除</Button>
                    }
                    <Button onClick={closeDialog}>キャンセル</Button>
                    <Button type="submit" onClick={handleSubmit}>確定</Button>
                </DialogActions>
            </Dialog>
            {/* yes no ダイアログ */}
            <ConfirmDialog
                open={isOpenConfirm}
                onClose={() => { 
                    setIsOpenConfirm(false); 
                    confirmCallBack(false)}
                }
                onConfirm={() => confirmCallBack(true)}
                title={confirmTitle}
                message={confirmMessage}
            />
        </>
    );
}

export default ScheduleControlDialog;